import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";

const SECTIONS = [
  {
    title: "1. The service",
    body: "Savy Kerno provides software for service businesses: AI voice agent configuration, a customer relationship manager (CRM), appointment booking, marketing content tools and reporting. Features marked “Coming soon”, “Pending setup” or “Not connected” are not active and require separate configuration before they can be used. We do not promise uninterrupted availability, and beta features may change.",
  },
  {
    title: "2. Your account",
    body: "You must provide accurate registration details, keep your password confidential and tell us promptly if you suspect unauthorised access. You are responsible for everything that happens under your account. You must be authorised to act for the business you register.",
  },
  {
    title: "3. Subscriptions and billing",
    body: "Paid plans are billed monthly in advance through our payment provider. The price agreed when you subscribe — including its price version — stays fixed for that subscription; changes to plan pricing never alter an existing subscription mid-term. You can cancel at any time from your billing settings; cancellation takes effect at the end of the current paid period. Failed payments may suspend access until resolved. Taxes may apply depending on your location.",
  },
  {
    title: "4. Acceptable use",
    body: "You must not use Savy Kerno to break the law, to harass or deceive, to send spam, to scrape or probe the platform, to upload malware, or to infringe others' rights. We may suspend accounts that abuse the service or other users.",
  },
  {
    title: "5. AI-generated content and AI agents",
    body: "Content and replies produced by AI features are generated automatically and can be wrong, incomplete or unsuitable. You are responsible for reviewing AI output before relying on it, publishing it or sending it to your customers. AI voice agent demonstrations in the browser are previews, not live telephone calls, unless a telephony provider is connected and enabled for your account.",
  },
  {
    title: "6. Calling and messaging consent",
    body: "Where calling or messaging features are enabled for your account, you are responsible for having a lawful basis and any required consent to contact each person, for honouring opt-outs and do-not-call requests, and for complying with the calling-time, marketing and privacy rules that apply in your region and your contacts' regions. Savy Kerno provides consent and suppression tools, but using them correctly is your responsibility.",
  },
  {
    title: "7. Your data",
    body: "You keep ownership of the data you add to your account (leads, appointments, content, settings). You grant us the right to store and process it to operate the service for you. Each account is isolated — other customers cannot see your data. See the Privacy Policy for details, retention and your rights.",
  },
  {
    title: "8. Third-party providers",
    body: "Some features rely on third-party services (for example payment processing, email delivery, telephony and social platforms). Those services are governed by their own terms, and features depending on them only work once the relevant provider is connected and configured for your account.",
  },
  {
    title: "9. Liability",
    body: "The service is provided “as is”. To the extent the law allows, we exclude implied warranties and are not liable for indirect or consequential losses, lost profits or lost data. Nothing in these terms excludes liability that cannot be excluded by law.",
  },
  {
    title: "10. Changes and contact",
    body: "We may update these terms; material changes will be signposted on this page with a new “last updated” date. Continued use after an update means you accept the revised terms. Questions: support@savykernoai.com.",
  },
  {
    title: "11. Cancellation and data deletion",
    body: "You can cancel your subscription at any time from the Billing page; access continues until the end of the paid period. Cancelling does not delete your data — you can delete records yourself in the dashboard, or ask us to export or delete your account data by emailing support@savykernoai.com from your account address. Billing and security records may be retained where required by law.",
  },
  {
    title: "12. Voice cloning and recordings",
    body: "You may only submit a voice sample for a custom voice if you own that voice or hold the speaker's written permission, and you must confirm this when submitting. We may reject or remove custom voice requests and suspend accounts that attempt unauthorised cloning. Call audio recording is off by platform default; if recording is ever enabled for your account you are responsible for informing callers.",
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <header className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="terms-brand">
            <BrandLogo />
            <span className="text-base font-bold tracking-tight">Savy Kerno</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-neutral-600 hover:text-[#111111]">
            Sign In
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-14">
        <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
        <p className="mt-2 text-xs text-neutral-400">Version 2026-10 · Last updated: October 2026</p>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-600">
          These terms govern your use of Savy Kerno. By creating an account you agree to them.
          This document is pending final legal review before paid launch.
        </p>
        <div className="mt-10 space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-base font-bold text-[#111111]">{s.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.body}</p>
            </section>
          ))}
        </div>
        <p className="mt-12 text-sm text-neutral-500">
          See also our <Link to="/privacy" className="font-semibold underline underline-offset-2">Privacy Policy</Link> and{" "}
          <Link to="/support" className="font-semibold underline underline-offset-2">Support page</Link>.
        </p>
      </main>
    </div>
  );
}
